/**
 * Ctrl+Z inside the task pane, forwarded to Word's own undo stack.
 *
 * The pane is a separate WebView with its own focus. A Ctrl+Z pressed while the
 * user is reading AiVA's reply never reaches Word — it goes to the WebView's
 * edit stack, so nothing happens and the document keeps the change. The user
 * has to click into the document first, which is not obvious and is exactly the
 * step the specification's undo acceptance criteria quietly assume (DE-03 AC-2,
 * "every write is reversible with a single Ctrl+Z", and the R-22 undo sweep).
 *
 * `Word.Document.undo()` drives the real host stack, so forwarding the shortcut
 * closes that gap. Three constraints shape the implementation:
 *
 * 1. **It is `WordApiDesktop 1.4`.** Word on the web and older desktop builds do
 *    not have it, so the probe runs before every call and the handler no-ops
 *    where it is missing rather than throwing. On those hosts the user keeps
 *    today's behaviour: click into the document, then Ctrl+Z.
 * 2. **Typing wins.** If the caret is in the composer — or any input, textarea
 *    or contenteditable — Ctrl+Z means "undo what I just typed". Forwarding it
 *    there would destroy the user's half-written prompt to revert a document
 *    change they did not ask to revert. The document stack is only reached from
 *    a non-editing focus.
 * 3. **One step, not many.** `undo()` defaults to a single action, matching the
 *    "a single Ctrl+Z" wording. A batch write that Word recorded as several
 *    entries still needs several presses — this forwards the keystroke, it does
 *    not try to model how many entries a given AiVA write produced.
 *
 * Word only. The other hosts have no equivalent API and no reported problem.
 */
import { useEffect } from "react";
import type { Channel } from "../../../shared/host";
import { logOfficeError } from "../../../telemetry/logHelpers";

/** True when focus is somewhere the browser's own undo is the right behaviour. */
function isTextEntry(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== "function") return false;
  return el.closest("input, textarea, [contenteditable='true']") !== null;
}

/** `false` only when the host positively reports the set as missing — an absent
 *  `requirements` object (tests, non-Office shells) is treated as "try it". */
function undoSupported(): boolean {
  return Office?.context?.requirements?.isSetSupported("WordApiDesktop", "1.4") !== false;
}

export function useWordUndoShortcut(host: Channel | null): void {
  useEffect(() => {
    if (host !== "word") return;
    if (typeof document === "undefined") return;

    const onKeyDown = (event: KeyboardEvent): void => {
      // Ctrl+Z (Cmd+Z on Mac). Shift+Ctrl+Z is redo — deliberately not handled;
      // forwarding it as an undo would be worse than doing nothing.
      const chord = (event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey;
      if (!chord || event.key.toLowerCase() !== "z") return;
      if (isTextEntry(event.target)) return;
      if (!undoSupported()) return;

      // Claim the keystroke only once we know we are going to act on it, so a
      // host without the API still lets the WebView do whatever it would have.
      event.preventDefault();
      void Word.run(async (context) => {
        // Cast: `undo` is WordApiDesktop, absent from the base Document type in
        // some @types/office-js versions.
        (context.document as unknown as { undo: (times?: number) => void }).undo();
        await context.sync();
      }).catch((err: unknown) => {
        // A failed undo is not worth a toast: the user pressed a shortcut, and
        // the document simply stays as it was.
        logOfficeError(err, "word-undo-shortcut");
      });
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [host]);
}
